// Shapes shared by the API, the web app, the wall widget and (later) the MCP server.
// Only schemas and types live here. No database or network code.
import { z } from "zod";
import catalog from "./plans.json" with { type: "json" };
import { EDITABLE_WORDS, WALL_LANGS, type WallLang, type WallWords, WORD_MAX } from "./words.js";

export * from "./words.js";

/** The languages a wall speaks (D-097). */
export const WallLanguage = z.enum(WALL_LANGS);
/** A post's description of what it shows, per language: Brandwall's AI's, or the brand's own. */
export const Description = z.object({ text: z.string(), by: z.enum(["ai", "brand"]) });
export const Descriptions = z.record(z.string(), Description);
export type Descriptions = z.infer<typeof Descriptions>;
/** The brand's own wording on a wall, per language: only the words it changed. */
export const WordOverridesSchema = z.partialRecord(
  WallLanguage,
  z.partialRecord(
    z.enum(EDITABLE_WORDS.map(([k]) => k) as [string, ...string[]]),
    z.string().trim().max(WORD_MAX),
  ),
);

/**
 * A change to some fields of a schema: every field optional, and no defaults filled in for the
 * fields left out (D-087). `.partial()` in zod 4 still fills in defaults, so merging its result
 * over the saved values put every field that wasn't sent back to its default.
 */
export function changesOf<S extends z.ZodRawShape>(schema: z.ZodObject<S>) {
  const shape: Record<string, z.ZodType> = {};
  for (const [k, v] of Object.entries(schema.shape) as [string, z.ZodType][])
    shape[k] = (v instanceof z.ZodDefault ? (v.unwrap() as z.ZodType) : v).optional();
  return z.object(shape) as unknown as ReturnType<z.ZodObject<S>["partial"]>;
}

export const Role = z.enum(["owner", "approver", "viewer"]);
export type Role = z.infer<typeof Role>;

export const Plan = z.enum(["free", "starter", "pro", "agency"]);
export type Plan = z.infer<typeof Plan>;

/** Every paid or free feature a plan can include. The labels live in plans.json. */
export const Feature = z.enum([
  "seasons",
  "ai_picks",
  "weekly_email",
  "product_walls",
  "product_dots",
  "mcp",
  "extra_accounts",
]);
export type Feature = z.infer<typeof Feature>;

const Limit = z.number().int().positive().nullable();
const PlanEntry = z.object({
  name: z.string(),
  price: z.number().min(0),
  for: z.string(),
  flag: z.string().nullable(),
  limits: z.object({ walls: Limit, seats: Limit, brands: Limit, monthlyViews: Limit }),
  /** Walls show "Powered by Brandwall". */
  credit: z.boolean(),
  features: z.array(Feature),
  /** What the app's plan page lists for the plan, in order. */
  bullets: z.array(z.string()),
  /**
   * What brandwall.app shows for the plan (D-084): the line in the pricing teaser, the button on
   * the pricing page and the bullets. The website says "Smart picks" and leaves out the weekly email.
   */
  website: z.object({ line: z.string(), cta: z.string(), bullets: z.array(z.string()) }),
});
export type PlanEntry = z.infer<typeof PlanEntry>;

/**
 * The plans, from plans.json (D-083): the one place to change what a plan costs, includes and
 * allows. Checked when it loads, so a typo stops the app instead of selling the wrong thing.
 */
export const PLAN_CATALOG = z
  .object({
    features: z.record(Feature, z.string()),
    plans: z.record(Plan, PlanEntry),
    notes: z.object({ everyPlan: z.string(), prices: z.string(), trial: z.string() }),
  })
  .parse(catalog);
export const PLANS = PLAN_CATALOG.plans;

const unlimited = (n: number | null) => n ?? Number.POSITIVE_INFINITY;

export const PLAN_LIMITS: Record<
  Plan,
  { walls: number; seats: number; credit: boolean; brands: number; monthlyViews: number }
> = Object.fromEntries(
  Plan.options.map((p) => [
    p,
    {
      walls: unlimited(PLANS[p].limits.walls),
      seats: unlimited(PLANS[p].limits.seats),
      brands: unlimited(PLANS[p].limits.brands),
      monthlyViews: unlimited(PLANS[p].limits.monthlyViews),
      credit: PLANS[p].credit,
    },
  ]),
) as never;

/** What each plan includes. Permission requests and Rights are on every plan. */
export const PLAN_FEATURES: Record<Plan, Feature[]> = Object.fromEntries(
  Plan.options.map((p) => [p, PLANS[p].features]),
) as never;

/** The cheapest plan with each feature, and its name, for "… is on the Pro plan". */
export const FEATURE_PLAN: Record<Feature, { plan: Plan; label: string }> = Object.fromEntries(
  Feature.options.map((f) => [
    f,
    {
      plan: Plan.options.find((p) => PLANS[p].features.includes(f)) ?? "agency",
      label: PLAN_CATALOG.features[f],
    },
  ]),
) as never;

/**
 * Instagram's own player for a public post, from its link (D-082). It plays videos and reels,
 * also those Instagram's API gives no video file for. Null for anything that isn't a post link.
 */
export function instagramEmbedUrl(permalink: string | null | undefined): string | null {
  const m = /^https:\/\/(?:www\.)?instagram\.com\/(?:[\w.]+\/)?(?:p|reel|reels|tv)\/([\w-]+)/.exec(
    permalink ?? "",
  );
  return m ? `https://www.instagram.com/p/${m[1]}/embed/` : null;
}

export function planAllows(plan: Plan, feature: Feature) {
  return PLAN_FEATURES[plan].includes(feature);
}

export const PLAN_PRICES: Record<Plan, number> = Object.fromEntries(
  Plan.options.map((p) => [p, PLANS[p].price]),
) as never;

export const PostStatus = z.enum(["to_review", "approved", "skipped", "discover"]);
/** Inbox tabs are the brand's own, tagged, mentioned and trusted posts; Discover has hashtag posts. */
export const InboxTab = z.enum(["to_review", "approved", "skipped", "live", "discover", "ignored"]);
export type InboxTab = z.infer<typeof InboxTab>;

export const Permission = z.enum([
  "none",
  "pending",
  "replied",
  "granted",
  "declined",
  "expired",
  "self_granted",
  "trusted",
  "own_post",
  /** The yes ran until a date that has passed (D-099): off the walls, files kept. */
  "ended",
]);
export type Permission = z.infer<typeof Permission>;

/** A calendar date, "2027-03-31". */
export const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * How long a new yes lasts by default (D-099): no limit, a number of months from the yes, or
 * until a date.
 */
export const ShowUntilDefault = z.union([z.enum(["none", "3m", "6m", "12m"]), DateOnly]);
export type ShowUntilDefault = z.infer<typeof ShowUntilDefault>;
export const LIVE_PERMISSIONS: readonly Permission[] = [
  "granted",
  "self_granted",
  "trusted",
  "own_post",
];

export const Source = z.enum(["own", "tag", "mention", "trusted", "hashtag", "manual"]);
export type Source = z.infer<typeof Source>;

export const TrustedRole = z.enum(["ambassador", "staff", "shop", "partner"]);

export const Username = z
  .string()
  .trim()
  .transform((v) => v.replace(/^@/, "").toLowerCase())
  .pipe(z.string().regex(/^[a-z0-9._]{1,30}$/, "That doesn't look like an Instagram username"));

export const Hashtag = z
  .string()
  .trim()
  .transform((v) => v.replace(/^#/, "").toLowerCase())
  .pipe(z.string().regex(/^[a-z0-9_]{1,100}$/, "Hashtags can only have letters, numbers and _"));

/** A hashtag for a label rule (D-100): letters of any alphabet, like #københavn, numbers and _. */
export const LabelHashtag = z
  .string()
  .trim()
  .transform((v) => v.replace(/^#/, "").toLowerCase())
  .pipe(
    z.string().regex(/^[\p{L}\p{N}_]{1,100}$/u, "Hashtags can only have letters, numbers and _"),
  );

export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const Brand = z.object({
  logoUrl: z.string().url().nullish(),
  colors: z.array(HexColor).max(6).default([]),
  fonts: z.array(z.string().max(60)).max(4).default([]),
});
export type Brand = z.infer<typeof Brand>;

export const Workspace = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  website: z.string().nullable(),
  plan: Plan,
  role: Role,
  brand: Brand,
  isDemo: z.boolean(),
  /** The plan the brand pays for. `plan` above includes a running trial. */
  paidPlan: Plan,
  trialPlan: Plan.nullable(),
  trialEndsAt: z.string().nullable(),
  weeklyEmail: z.boolean(),
  timezone: z.string(),
  /** Show Instagram likes and comments on posts in Brandwall. */
  showEngagement: z.boolean(),
});
export type Workspace = z.infer<typeof Workspace>;

/** The terms people accept. Bump it when the terms change in a way people must accept again. */
export const TERMS_VERSION = "2026-10-01";

export const Me = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /** False until the person has accepted the current terms. */
    termsAccepted: z.boolean(),
    /** Super admin (ADMIN_EMAILS): sees every customer. */
    admin: z.boolean(),
  }),
  workspaces: z.array(Workspace),
});
export type Me = z.infer<typeof Me>;

export const InstagramStatus = z.object({
  connected: z.boolean(),
  status: z.enum(["connected", "broken"]).nullable(),
  username: z.string().nullable(),
  profilePictureUrl: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  /**
   * Every Instagram account on the brand (D-078): the main one first, then extra accounts that
   * only search hashtags. Each account can search 30 hashtags a week.
   */
  accounts: z.array(
    z.object({
      id: z.string().uuid(),
      role: z.enum(["main", "extra"]),
      username: z.string(),
      profilePictureUrl: z.string().nullable(),
      status: z.enum(["connected", "broken"]),
      lastError: z.string().nullable(),
      hashtags: z.array(z.string()),
    }),
  ),
  /** Whether the plan lets the brand add accounts for hashtag searches (Pro and Agency). */
  extraAllowed: z.boolean(),
});
export type InstagramStatus = z.infer<typeof InstagramStatus>;

export const SyncStatus = z.object({
  running: z.boolean(),
  found: z.number(),
  added: z.number(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
  totalPosts: z.number(),
});
export type SyncStatus = z.infer<typeof SyncStatus>;

/** A brand's own label for posts (D-100). */
export const Label = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** For the snippet (data-label) and visitors' buttons. */
  slug: z.string(),
  /** Posts with it, confirmed; and suggested by the AI, waiting for the brand. */
  posts: z.number().default(0),
  suggested: z.number().default(0),
});
export type Label = z.infer<typeof Label>;
export const LabelName = z.string().trim().min(1).max(40);

export const Post = z.object({
  id: z.string().uuid(),
  source: Source,
  creator: z.string(),
  caption: z.string().nullable(),
  mediaType: z.enum(["image", "video", "carousel"]),
  permalink: z.string().nullable(),
  postedAt: z.string().nullable(),
  previewUrl: z.string().nullable(),
  status: PostStatus,
  permission: Permission,
  live: z.boolean(),
  skipReason: z.string().nullable(),
  pinned: z.boolean(),
  hidden: z.boolean(),
  score: z.number().nullable(),
  scores: z.object({ quality: z.number(), product: z.number(), brandFit: z.number() }).nullable(),
  scoreReason: z.string().nullable(),
  flags: z.array(z.string()),
  topPick: z.boolean(),
  autoSkipped: z.boolean(),
  hashtag: z.string().nullable(),
  /** Likes and comments on Instagram; null when Instagram doesn't share them. */
  likes: z.number().nullable(),
  comments: z.number().nullable(),
  productCount: z.number(),
  /**
   * Brandwall's own copy of the video, for people in the workspace only: the full copy once the
   * post has permission, before that the private copy kept at collection (D-090). Null when
   * there is none.
   */
  videoUrl: z.string().nullable(),
  /** What Instagram says about the sound: MUSIC is a licensed song. Null when it doesn't say. */
  audioType: z.string().nullable(),
  /** Brandwall has the video file: it goes up in full, with its sound, once there's a yes. */
  videoSaved: z.boolean(),
  /** What the picture shows, per language, for screen readers on the walls (D-097). */
  descriptions: Descriptions.default({}),
  /** The brand's labels on it (D-100): confirmed, or suggested by the AI. */
  labels: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        status: z.enum(["confirmed", "suggested"]),
      }),
    )
    .default([]),
  /** The yes runs until this date: the post leaves the walls on it (D-099). */
  showUntil: DateOnly.nullable().default(null),
  permissionEndedAt: z.string().nullable().default(null),
  /** Discover: saved to ask later ("Want it" when going through posts one by one). */
  saved: z.boolean(),
  /** The open permission request, when Brandwall is asking. */
  request: z
    .object({
      id: z.string().uuid(),
      route: z.enum(["comment", "dm", "manual"]),
      status: z.string(),
      needsPerson: z.boolean(),
      /** "extend": asking for a longer yes (D-099). */
      purpose: z.enum(["ask", "extend"]).default("ask"),
    })
    .nullable(),
});
export type Post = z.infer<typeof Post>;

export const PostList = z.object({
  posts: z.array(Post),
  counts: z.record(InboxTab, z.number()),
  nextCursor: z.string().nullable(),
  /** Discover only: hashtag posts not shown yet, because their picture isn't stored. */
  waiting: z
    .object({
      /** Still being prepared: picture and AI check. */
      preparing: z.number(),
      /** Instagram shares no picture of them (usually videos without a file or cover). */
      noPicture: z.number(),
    })
    .optional(),
  /** Discover only: saved to ask later, and not gone through yet. */
  found: z.object({ saved: z.number(), unsorted: z.number() }).optional(),
});
export type PostList = z.infer<typeof PostList>;

/** Every write that an AI could also do returns a sentence it can repeat as is. */
export const ActionResult = z.object({
  summary: z.string(),
  ids: z.array(z.string()).default([]),
  /** True when the action waits for a person's approval instead of happening now. */
  waiting: z.boolean().optional(),
  approvalId: z.string().uuid().optional(),
});
export type ActionResult = z.infer<typeof ActionResult>;

export const AutoApprove = z.object({
  /** The brand's own Instagram posts go on its walls by themselves. */
  own: z.boolean(),
  /** Posts from trusted accounts go on the walls by themselves... */
  trusted: z.boolean(),
  /** ...but only those with one of these hashtags. Empty: all their posts. */
  hashtags: z.array(z.string()),
});
export type AutoApprove = z.infer<typeof AutoApprove>;

export const Sources = z.object({
  tagsEnabled: z.boolean(),
  autoApprove: AutoApprove,
  hashtags: z.array(z.string()),
  hashtagLimit: z.number(),
  trusted: z.array(
    z.object({
      id: z.string().uuid(),
      username: z.string(),
      role: TrustedRole,
      lastError: z.string().nullable(),
      /** After this date their new posts no longer go up by themselves (D-099). */
      trustedUntil: DateOnly.nullable().default(null),
    }),
  ),
});
export type Sources = z.infer<typeof Sources>;

// Taste (screen 08): rules in plain words, turned into chips.
export const TasteChip = z.object({
  kind: z.enum(["require", "avoid", "prefer"]),
  label: z.string(),
  topic: z.string().nullable(),
  words: z.array(z.string()),
});
export type TasteChip = z.infer<typeof TasteChip>;

export const Taste = z.object({
  rules: z.string().max(2000).default(""),
  chips: z.array(TasteChip).default([]),
  /** Overall score from which a post is a top pick. */
  threshold: z.number().int().min(50).max(100).default(80),
  competitors: z.array(z.string().max(60)).max(30).default([]),
});
export type Taste = z.infer<typeof Taste>;

export const TasteView = Taste.extend({
  examples: z.array(
    z.object({
      postId: z.string().uuid(),
      kind: z.enum(["love", "never"]),
      previewUrl: z.string().nullable(),
    }),
  ),
  engine: z.enum(["basic", "claude"]),
});
export type TasteView = z.infer<typeof TasteView>;

// Rights (screens 12 to 17).
export const RequestStatus = z.enum([
  "draft",
  "pending",
  "replied",
  "granted",
  "declined",
  "expired",
  "withdrawn",
]);
export type RequestStatus = z.infer<typeof RequestStatus>;
export const Lang = z.enum(["en", "da", "sv", "no", "fi"]);
export type Lang = z.infer<typeof Lang>;

const YesHashtag = z.string().regex(/^[a-z0-9_]{2,40}$/, "Use letters, numbers and _ only");
export const RightsSettings = z.object({
  /** The first of `hashtags`. */
  hashtag: YesHashtag,
  /** Up to three yes hashtags; comments take turns, and any of them is a yes (D-072). */
  hashtags: z.array(YesHashtag).min(1).max(3),
  /** The brand's own comment wording, one of several that take turns. Empty: none. */
  comment: z.string().max(500),
  /** The brand's own direct message, one of several that take turns. Empty: none. */
  dm: z.string().max(1000),
  termsUrl: z.string().max(300),
  askByComment: z.boolean(),
  signature: z.string().max(80),
  /** How long a new yes lasts (D-099). */
  showUntil: ShowUntilDefault.default("none"),
});
export type RightsSettings = z.infer<typeof RightsSettings>;

export const PermissionRequest = z.object({
  id: z.string().uuid(),
  postId: z.string().uuid().nullable(),
  kind: z.enum(["post", "trusted_invite"]),
  creator: z.string(),
  route: z.enum(["comment", "dm", "manual"]),
  status: RequestStatus,
  attempt: z.number(),
  language: Lang,
  message: z.string(),
  link: z.string().nullable(),
  needsPerson: z.boolean(),
  /** A comment Brandwall will post later, paced to look human (D-072). */
  scheduledAt: z.string().nullable(),
  previewUrl: z.string().nullable(),
  permalink: z.string().nullable(),
  sentAt: z.string().nullable(),
  sentBy: z.string().nullable(),
  expiresAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  answerVia: z.string().nullable(),
  answerText: z.string().nullable(),
  answerLink: z.string().nullable(),
  answeredBy: z.string().nullable(),
  givenBy: z.string().nullable(),
  note: z.string().nullable(),
  /** What can happen next: send, ask once more, or nothing (declined, asked twice). */
  canAskAgain: z.boolean(),
  createdAt: z.string(),
  /** The date the creator was told the post may be shown until (D-099). */
  showUntil: DateOnly.nullable().default(null),
  /** "extend": asking for a longer yes before the current one ends. */
  purpose: z.enum(["ask", "extend"]).default("ask"),
});
export type PermissionRequest = z.infer<typeof PermissionRequest>;

export const RightsRow = z.object({
  postId: z.string().uuid(),
  creator: z.string(),
  previewUrl: z.string().nullable(),
  permalink: z.string().nullable(),
  /** "trusted" and "own_post" rows need no request. */
  status: z.enum([...RequestStatus.options, "trusted", "own_post"]),
  request: PermissionRequest.nullable(),
});
export type RightsRow = z.infer<typeof RightsRow>;

export const RightsList = z.object({
  rows: z.array(RightsRow),
  counts: z.record(z.string(), z.number()),
  settings: RightsSettings,
  sentToday: z.number(),
  sentThisWeek: z.number(),
  /** How fast the brand asks (D-072): Instagram may slow accounts that ask too fast. */
  pace: z.object({
    commentsQueued: z.number(),
    nextCommentAt: z.string().nullable(),
    commentsLastHour: z.number(),
    messagesLastHour: z.number(),
  }),
});
export type RightsList = z.infer<typeof RightsList>;

/** Everything the brand's AI needs to ask one creator (Appendix A section 3). */
export const PermissionPack = z.object({
  creator: z.string(),
  profileUrl: z.string().nullable(),
  postUrl: z.string().nullable(),
  postId: z.string().uuid(),
  route: z.enum(["comment", "dm"]),
  message: z.string(),
  link: z.string().nullable(),
  language: Lang,
  standing: z.enum(["never_asked", "asked_once", "do_not_ask", "granted", "draft_ready"]),
  reason: z.string().nullable(),
  requestId: z.string().uuid().nullable(),
  expiresAt: z.string().nullable(),
});
export type PermissionPack = z.infer<typeof PermissionPack>;

/** What the creator sees on the approval page (screen 15). */
export const ApprovalPage = z.object({
  brand: z.object({ name: z.string(), logoUrl: z.string().nullable(), site: z.string() }),
  creator: z.string(),
  creatorKnown: z.boolean(),
  kind: z.enum(["post", "trusted_invite"]),
  language: Lang,
  termsUrl: z.string().nullable(),
  imageUrl: z.string().nullable(),
  permalink: z.string().nullable(),
  status: RequestStatus,
  answeredAt: z.string().nullable(),
  /** The post may be shown until this date (D-099); null: no end date. */
  showUntil: DateOnly.nullable().default(null),
  /** Asking to keep showing a post that's already shown. */
  extend: z.boolean().default(false),
});
export type ApprovalPage = z.infer<typeof ApprovalPage>;

// Products (screens 18 to 20).
export const Shop = z.object({
  connected: z.boolean(),
  kind: z.enum(["shopify", "feed"]).nullable(),
  url: z.string().nullable(),
  currency: z.string().nullable(),
  status: z.enum(["connected", "broken"]).nullable(),
  productCount: z.number(),
  lastSyncedAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type Shop = z.infer<typeof Shop>;

export const Product = z.object({
  id: z.string().uuid(),
  title: z.string(),
  variantTitle: z.string().nullable(),
  handle: z.string().nullable(),
  price: z.number().nullable(),
  currency: z.string().nullable(),
  imageUrl: z.string().nullable(),
  url: z.string().nullable(),
  available: z.boolean(),
});
export type Product = z.infer<typeof Product>;

export const ProductTag = Product.extend({
  x: z.number(),
  y: z.number(),
  status: z.enum(["confirmed", "suggested"]),
  confidence: z.number().nullable(),
});
export type ProductTag = z.infer<typeof ProductTag>;

export const Freshness = z.object({
  newestAgeDays: z.number().nullable(),
  averageAgeDays: z.number().nullable(),
  score: z.number(),
  tip: z.string().nullable(),
});
export type Freshness = z.infer<typeof Freshness>;

export const Layout = z.enum(["grid", "carousel", "masonry", "strip"]);
export type Layout = z.infer<typeof Layout>;

export const WallDesign = z.object({
  heading: z.string().max(120).default("Seen on you"),
  rows: z.number().int().min(1).max(6).default(2),
  columns: z.number().int().min(2).max(6).default(4),
  corners: z.number().int().min(0).max(24).default(10),
  spacing: z.number().int().min(0).max(32).default(8),
  background: HexColor.default("#ffffff"),
  text: HexColor.default("#10302a"),
  accent: HexColor.default("#10302a"),
  font: z.string().max(60).default("inherit"),
  productDots: z.boolean().default(true),
  /** Grid and masonry: fill the page, then keep adding rows as visitors scroll. Rows is ignored. */
  infinite: z.boolean().default(false),
});
export type WallDesign = z.infer<typeof WallDesign>;

/** A wall's own words, per language (D-098): the headline, the text under it and the button. */
export const WallCopy = z.object({
  heading: z.string().trim().max(120).optional(),
  intro: z.string().trim().max(300).optional(),
  button: z.string().trim().max(40).optional(),
});
export type WallCopy = z.infer<typeof WallCopy>;

/**
 * Everything a wall shows around its posts (D-098), besides the headline's on/off and main
 * wording, which stay in the design (`heading`, empty for none). All parts start off.
 */
export const WallText = z.object({
  /** The text under the headline. */
  intro: z.boolean().default(false),
  /** "Share your moments with @brand or #hashtag", from the brand's account and hashtags. */
  tagUs: z.boolean().default(false),
  /** One button with the brand's own words and link. */
  button: z.boolean().default(false),
  buttonUrl: z.string().trim().max(500).default(""),
  /** Where the text, tag us line and button sit: above or below the posts. */
  place: z.enum(["above", "below"]).default("above"),
  align: z.enum(["left", "center"]).default("left"),
  /** "site": the headline and text take the website's own heading and text style. */
  style: z.enum(["site", "wall"]).default("site"),
  /** The words per language; a missing one uses the brand's own language, then the default. */
  copy: z.partialRecord(WallLanguage, WallCopy).default({}),
});
export type WallText = z.infer<typeof WallText>;

/** The words around the posts as a shopper's page gets them, already in its language. */
export const PublicWallText = z.object({
  heading: z.string().nullable(),
  intro: z.string().nullable(),
  tagUs: z.object({ account: z.string().nullable(), hashtag: z.string().nullable() }).nullable(),
  button: z.object({ label: z.string(), url: z.string() }).nullable(),
  place: z.enum(["above", "below"]),
  align: z.enum(["left", "center"]),
  style: z.enum(["site", "wall"]),
  /** The language of the brand's own words where it isn't the page's (a fallback). */
  langs: z
    .object({ heading: WallLanguage, intro: WallLanguage, button: WallLanguage })
    .partial()
    .default({}),
});
export type PublicWallText = z.infer<typeof PublicWallText>;

/** A change to a wall's words, per language; an empty part is removed. */
export type WallCopyPatch = Partial<Record<WallLang, Partial<Record<keyof WallCopy, string>>>>;

export function applyCopy(current: WallText["copy"], patch: WallCopyPatch): WallText["copy"] {
  const out: WallText["copy"] = JSON.parse(JSON.stringify(current ?? {}));
  for (const [lang, parts] of Object.entries(patch) as [WallLang, Record<string, string>][]) {
    const own: Record<string, string> = { ...(out[lang] ?? {}) };
    for (const [part, v] of Object.entries(parts ?? {})) {
      // Kept as typed (the editor shows it while typing); the server trims what it saves.
      if (v?.trim()) own[part] = v;
      else delete own[part];
    }
    if (Object.keys(own).length) out[lang] = own;
    else delete out[lang];
  }
  return out;
}

/** One part of a wall's words in a language: that language's, else the brand's, else none. */
export function copyFor(t: WallText, part: keyof WallCopy, lang: WallLang, brand: WallLang) {
  return t.copy[lang]?.[part]?.trim() || t.copy[brand]?.[part]?.trim() || null;
}

/** The language a part's words are in, when they're the brand's and not in the page's language. */
function copyLang(t: WallText, part: keyof WallCopy, lang: WallLang, brand: WallLang) {
  return !t.copy[lang]?.[part]?.trim() && t.copy[brand]?.[part]?.trim() ? brand : undefined;
}

/** Whether a link goes to Instagram: the button's usual words fit it. */
export const isInstagramLink = (url: string) => /^https:\/\/(www\.)?instagram\.com\//i.test(url);

/** The words around a wall's posts as one page gets them, in its language (D-098). */
export function wallTextFor(
  heading: string,
  t: WallText,
  lang: WallLang,
  brand: WallLang,
  words: WallWords,
  tag: { account: string | null; hashtag: string | null },
): PublicWallText {
  // The button links to the brand's Instagram until the brand gives it another link.
  const given = /^https?:\/\/\S+$/i.test(t.buttonUrl) ? t.buttonUrl : null;
  const url =
    given ?? (tag.account ? `https://www.instagram.com/${tag.account.replace(/^@/, "")}/` : null);
  const label =
    copyFor(t, "button", lang, brand) ?? (url && isInstagramLink(url) ? words.followUs : null);
  const langs: PublicWallText["langs"] = {};
  for (const part of ["heading", "intro", "button"] as const) {
    const l = copyLang(t, part, lang, brand);
    if (l) langs[part] = l;
  }
  return {
    // No headline when the brand switched it off; the default one in every wall language.
    heading: heading
      ? (copyFor(t, "heading", lang, brand) ??
        (heading === WallDesign.shape.heading.parse(undefined) ? words.seenOnYou : heading))
      : null,
    intro: t.intro ? copyFor(t, "intro", lang, brand) : null,
    tagUs: t.tagUs && (tag.account || tag.hashtag) ? tag : null,
    button: t.button && url && label ? { label, url } : null,
    place: t.place,
    align: t.align,
    style: t.style,
    langs,
  };
}

/**
 * Video and sound on a wall (D-092): "no_songs" plays videos' own sound but mutes those with a
 * licensed song from Instagram (or where Brandwall can't tell); "all" plays every sound; "none"
 * mutes every video.
 */
export const WallSound = z.enum(["no_songs", "all", "none"]);
export type WallSound = z.infer<typeof WallSound>;

/** Video tiles (D-094, D-101): "play" silently while the mouse is on them, or a "still" picture. */
export const WallTiles = z.enum(["play", "still"]);
export type WallTiles = z.infer<typeof WallTiles>;

/** One video on one wall: "on" or "off" overrides the wall's setting. */
export const PostSound = z.enum(["on", "off"]);
export type PostSound = z.infer<typeof PostSound>;

/**
 * Whether a video plays without sound. `song` is true when Instagram says the video uses a
 * licensed song, null when Brandwall can't tell (treated like a song).
 */
export function playsMuted(wall: WallSound, post: PostSound | null, song: boolean | null) {
  if (post) return post === "off";
  if (wall === "all") return false;
  if (wall === "none") return true;
  return song !== false;
}

/** Instagram's media_audio_type as a song or not; null when it doesn't say. */
export function isSong(audioType: string | null | undefined): boolean | null {
  if (!audioType) return null;
  return /MUSIC/i.test(audioType);
}

/** One full row of posts for a layout: the default minimum before a wall shows (D-095). */
export function fullRow(layout: Layout, columns: number) {
  return layout === "carousel" ? 4 : layout === "strip" ? 6 : columns;
}

/** How many live posts a wall needs before it shows: 0 means it always shows. */
export function postsNeeded(minPosts: number | null, layout: Layout, columns: number) {
  return minPosts ?? fullRow(layout, columns);
}

/** Whether a wall with this many posts is hidden for having too few (D-095). */
export function tooFew(count: number, need: number) {
  return need > 0 && count < need;
}

export const WallKind = z.enum(["normal", "product", "seasonal"]);
export type WallKind = z.infer<typeof WallKind>;

export const Wall = z.object({
  id: z.string().uuid(),
  publicId: z.string(),
  name: z.string(),
  kind: WallKind,
  selection: z.enum(["all", "picked"]),
  freshness: Freshness,
  layout: Layout,
  design: WallDesign,
  sound: WallSound,
  tiles: WallTiles,
  /** Hide the wall below this many posts: null is one full row (the default), 0 never hides. */
  minPosts: z.number().int().min(0).max(48).nullable(),
  /** Hidden on the brand's site for having too few posts (D-095). Null on product-page walls. */
  hidden: z.object({ have: z.number(), need: z.number() }).nullable(),
  /** The wall's language (D-097): null follows the page it's on. */
  language: WallLanguage.nullable(),
  /** The brand's own wording, per language. */
  words: WordOverridesSchema,
  /** The languages posts are described in for this wall: the one it's fixed to, or the brand's
   * and those its pages are shown in. */
  languages: z.array(WallLanguage),
  /** The languages of the pages it's shown on: seen on two days, the last in the past 30. */
  seenIn: z.array(WallLanguage),
  /** The words around the posts (D-098). */
  text: WallText,
  /** Which posts (D-100): empty for all posts, else only posts with one of these labels. */
  labels: z.array(z.string().uuid()),
  /** Buttons for visitors to switch between the wall's labels. */
  labelButtons: z.boolean(),
  /** The brand's own language, from its website: the main version of the wall's words. */
  brandLanguage: WallLanguage,
  /** A headline and text suggested in the brand's voice for a new wall, until used or dismissed. */
  suggestion: z
    .object({ heading: z.string(), intro: z.string(), lang: WallLanguage.optional() })
    .nullable(),
  status: z.enum(["draft", "live"]),
  pages: z.array(z.string()),
  publishedAt: z.string().nullable(),
  postCount: z.number(),
  newestPostAt: z.string().nullable(),
});
export type Wall = z.infer<typeof Wall>;

/** A product dot as shoppers see it. Sold-out and removed products are left out. */
export const PublicProduct = z.object({
  n: z.number(),
  x: z.number(),
  y: z.number(),
  title: z.string(),
  price: z.string().nullable(),
  url: z.string().nullable(),
  image: z.string().nullable(),
  variantId: z.string().nullable(),
  /** Brandwall's id, sent back with clicks so Insights can count them per product. */
  id: z.string().default(""),
});
export type PublicProduct = z.infer<typeof PublicProduct>;

/** What a brand's website receives. No personal data beyond the public post credit. */
export const PublicWallPost = z.object({
  id: z.string(),
  type: z.enum(["image", "video"]),
  src: z.string(),
  srcset: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  video: z.string().nullable(),
  /** A short, small, silent loop for tiles that play on mouse-over; null: use the video. */
  tile: z.string().nullable().default(null),
  alt: z.string(),
  caption: z.string().nullable(),
  creator: z.string(),
  permalink: z.string().nullable(),
  postedAt: z.string().nullable(),
  products: z.array(PublicProduct).default([]),
  /** Tile size on grid walls: 1 is one cell, 2 is 2x2, 3 is 3x3. */
  size: z.number().int().min(1).max(3).default(1),
  /** Kept in its place on the wall by the brand. */
  fixed: z.boolean().default(false),
  /** A video that plays without sound; visitors can't switch it on (D-092). */
  muted: z.boolean().default(false),
});
export type PublicWallPost = z.infer<typeof PublicWallPost>;

export const PublicWall = z.object({
  id: z.string(),
  layout: Layout,
  design: WallDesign,
  credit: z.boolean(),
  /** The shop, so "Add to bag" works when the wall runs on that shop. */
  shop: z
    .object({ kind: z.enum(["shopify", "feed"]), origin: z.string() })
    .nullable()
    .default(null),
  posts: z.array(PublicWallPost),
  /** Video tiles play silently while the mouse is on them, or show a still picture (D-094, D-101). */
  tiles: WallTiles.default("play"),
  /** Where the next posts start, for walls that keep loading as visitors scroll. Null: no more. */
  next: z.number().nullable().default(null),
  /**
   * Set when the wall shows nothing for now (D-083): "views" when a plan's monthly views are
   * used up, "walls" when the plan has fewer walls than the brand has live, "few" when the wall
   * has fewer posts than the brand's minimum (D-095).
   */
  paused: z.enum(["views", "walls", "few"]).nullable().default(null),
  /** The language the words are in (D-097), and the words. */
  lang: WallLanguage.default("en"),
  words: z.record(z.string(), z.string()).default({}),
  /** The headline, text, tag us line and button (D-098); null for older answers. */
  text: PublicWallText.nullable().default(null),
  /** Labels as buttons for visitors (D-100), and the one shown now (null: all). */
  labels: z.array(z.object({ slug: z.string(), name: z.string() })).default([]),
  label: z.string().nullable().default(null),
});
export type PublicWall = z.infer<typeof PublicWall>;

/**
 * The wall as the design screen shows it: the public wall, plus what the screen needs to show
 * sound changes before they're saved (D-092).
 */
export const WallPreview = PublicWall.extend({
  sound: z.object({
    wall: WallSound,
    /** Per video post: whether it uses a song (null: can't tell) and its own setting. */
    posts: z.record(
      z.string(),
      z.object({ song: z.boolean().nullable(), sound: PostSound.nullable() }),
    ),
  }),
  /** Per post, what it shows in each language, for the post panel (D-097). */
  descriptions: z.record(z.string(), Descriptions).default({}),
  /** Per post, its labels, for the post panel (D-100). */
  postLabels: z
    .record(
      z.string(),
      z.array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          status: z.enum(["confirmed", "suggested"]),
        }),
      ),
    )
    .default({}),
  /** The brand's account and hashtag, for the tag us line (D-098). */
  tag: z
    .object({ account: z.string().nullable(), hashtag: z.string().nullable() })
    .default({ account: null, hashtag: null }),
});
export type WallPreview = z.infer<typeof WallPreview>;

/** Numbers for a demo's UGC report (D-068), measured from public posts when the demo is made. */
export const DemoStats = z.object({
  posts: z.number(),
  creators: z.number(),
  likes: z.number(),
  comments: z.number(),
  avgLikes: z.number().nullable(),
  avgComments: z.number().nullable(),
  /** Posts a week across the time the posts cover. */
  perWeek: z.number().nullable(),
  newest: z.string().nullable(),
  oldest: z.string().nullable(),
});
export type DemoStats = z.infer<typeof DemoStats>;

export const DemoReport = z.object({
  collectedAt: z.string(),
  account: z
    .object({
      username: z.string(),
      followers: z.number().nullable(),
      /** Likes and comments per post as a share of followers, in percent. */
      engagementRate: z.number().nullable(),
      stats: DemoStats,
    })
    .nullable(),
  /**
   * Per hashtag: the posts about the brand (all posts when Claude didn't rate them), and how
   * many recent public posts were looked at (`looked`). Instagram doesn't say how many posts a
   * hashtag has, so these are a sample (D-073).
   */
  hashtags: z.array(DemoStats.extend({ tag: z.string(), looked: z.number().optional() })),
  /** Posts by others (hashtags) about the brand, each counted once. */
  community: DemoStats,
  /** How many posts by others were looked at, about the brand or not. */
  communityLooked: z.number().optional(),
  /** Customer posts by how many likes they got: under 10, 10 to 99, 100 to 999, and so on. */
  likeBuckets: z.array(z.object({ label: z.string(), posts: z.number() })).optional(),
  /** Posts a month, customers' and the brand's own, for the months the posts cover. */
  months: z
    .array(z.object({ month: z.string(), community: z.number(), own: z.number() }))
    .optional(),
  /** What Claude looked for when it picked the pictures for the demo wall (D-071). */
  ai: z
    .object({
      sells: z.string(),
      looked: z.number(),
      picked: z.number(),
      /** Customer posts Claude found to be about the brand (D-073). */
      relevant: z.number().optional(),
    })
    .nullable()
    .optional(),
});
export type DemoReport = z.infer<typeof DemoReport>;

/** The code in an Instagram post's link (/p/CODE/, /reel/CODE/), or null. */
export function instagramShortcode(link: string): string | null {
  return /instagram\.com\/(?:[^/?#]+\/)?(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/.exec(link)?.[1] ?? null;
}

const httpsLink = z
  .string()
  .trim()
  .url()
  .max(2000)
  .refine((u) => u.startsWith("https://"), "Use an https link.");

/**
 * One public Instagram post Claude found and adds to a demo through the admin MCP (D-085, D-089).
 */
export const DemoPost = z.object({
  link: httpsLink
    .refine(
      (u) => /^https:\/\/(www\.)?instagram\.com\//.test(u) && instagramShortcode(u) !== null,
      "Use the post's Instagram link, like https://www.instagram.com/p/CODE/",
    )
    .describe("The post on Instagram, like https://www.instagram.com/p/CODE/ or /reel/CODE/."),
  id: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{1,80}$/)
    .optional()
    .describe("Instagram's id for the post. Left out, the code in the link is used."),
  type: z.enum(["image", "video", "carousel"]).default("image"),
  imageUrl: httpsLink.describe(
    "The picture, or a video's cover picture: Instagram's own image link (cdninstagram.com or fbcdn.net), or any public https picture.",
  ),
  videoUrl: httpsLink.optional().describe("A video's file, when there is one."),
  caption: z.string().max(5000).optional(),
  tagged: z
    .boolean()
    .optional()
    .describe(
      "The brand's account is tagged in the picture (a photo tag, as on the brand's Tagged tab). Such a post counts for the wall even when its caption doesn't name the brand.",
    ),
  postedAt: z
    .union([z.string().max(40), z.number()])
    .optional()
    .describe("When it was posted: a date like 2026-09-30T12:00:00Z, or seconds since 1970."),
  username: z.string().trim().max(31).optional().describe("Who posted it, without @."),
  likes: z.number().int().min(0).optional(),
  comments: z.number().int().min(0).optional(),
});
export type DemoPost = z.infer<typeof DemoPost>;

/**
 * Posts for a demo, found by Claude (D-085, D-089): the brand's account and its
 * latest posts, and the posts found under each hashtag. Each call adds to the posts before.
 */
export const DemoPostsInput = z.object({
  account: z
    .object({
      username: z.string().trim().min(1).max(31).describe("The brand's Instagram, without @."),
      followers: z.number().int().min(0).optional(),
      posts: z
        .array(DemoPost)
        .max(60)
        .default([])
        .describe("The brand's own latest posts, up to 60. They only feed the report's numbers."),
    })
    .optional()
    .describe("The brand's own Instagram account."),
  hashtags: z
    .array(
      z.object({
        tag: z.string().trim().min(1).max(60).describe("The hashtag, with or without #."),
        posts: z.array(DemoPost).max(300),
      }),
    )
    .max(10)
    .default([])
    .describe(
      "Customers' posts, by the hashtag they were found under. Posts found by their photo tag of the brand go under the brand's main hashtag, with tagged.",
    ),
  replace: z
    .boolean()
    .default(false)
    .describe("Forget the posts added before, and keep only these."),
});
export type DemoPostsInput = z.infer<typeof DemoPostsInput>;

/** An Instagram handle, with or without @. Letters, numbers, dots and underscores. */
const igHandle = z
  .string()
  .trim()
  .regex(/^@?[A-Za-z0-9._]{1,30}$/, "Use the Instagram handle, like @rains.");

const instagramPost = httpsLink.refine(
  (u) => /^https:\/\/(www\.)?instagram\.com\//.test(u) && instagramShortcode(u) !== null,
  "Use the post's Instagram link, like https://www.instagram.com/p/CODE/",
);

/** Where an account showed a brand (D-103). */
export const MENTION_MATCHES = ["caption", "photo_tag", "comment", "competitor_post"] as const;

/** Flags on an account (D-109). */
export const PROFILE_FLAGS = ["engagement_pod", "bought_likes", "brand_safety"] as const;
/** Why an account was turned down (D-109). */
export const REJECT_REASONS = [
  "store",
  "brand_account",
  "too_small",
  "too_big",
  "competitor_employee",
  "inactive",
  "off_niche",
  "fake_engagement",
  "brand_safety",
  "private",
  "other",
] as const;
/** How a post is labelled as advertising (D-109). */
export const DISCLOSURES = ["ad", "gifted", "paid_partnership"] as const;

const when = z
  .string()
  .trim()
  .max(40)
  .refine((v) => !Number.isNaN(Date.parse(v)), "Use a date like 2026-10-05.");

/**
 * One Instagram account Claude researched (D-103, D-109). Every account looked at is saved,
 * chosen or not, so research isn't repeated. What's left out stays as it was.
 */
export const ResearchProfile = z.object({
  igUserId: z
    .string()
    .trim()
    .regex(/^[0-9]{1,30}$/, "Use Instagram's numeric user id.")
    .describe(
      'Instagram\'s numeric user id for the account (it stays when the handle changes). It\'s in the profile page\'s data, e.g. "profile_id" or "id" in the page source.',
    ),
  handle: igHandle.describe("The account's handle, with or without @."),
  measuredAt: when
    .optional()
    .describe(
      "When the numbers below were read off Instagram. Left out: now. A number is only replaced by one measured at the same time or later.",
    ),
  followers: z.number().int().min(0).optional(),
  accountType: z.enum(["personal", "creator", "business"]).optional(),
  location: z
    .string()
    .trim()
    .max(120)
    .optional()
    .describe('Where the account is, as precisely as it says: "Copenhagen", "Madrid", "Basel".'),
  country: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, "Use the two-letter country code, like DK.")
    .optional()
    .describe("The country, as a two-letter code: DK, ES, CH."),
  languages: z
    .array(
      z
        .string()
        .trim()
        .regex(/^[a-z]{2}$/, "Use two-letter language codes, like da."),
    )
    .max(5)
    .optional()
    .describe('The languages it writes in, as two-letter codes: ["da", "en"].'),
  niches: z
    .array(z.string().trim().min(1).max(40))
    .max(10)
    .default([])
    .describe('What the account is about, a word or two each: "running", "mum life", "denim".'),
  engagement: z
    .number()
    .min(0)
    .max(10000)
    .optional()
    .describe(
      "Likes and comments a post, as a percentage of followers, e.g. 3.4. Left out, it's worked out from the averages and followers when likes aren't hidden.",
    ),
  avgLikes: z.number().min(0).optional().describe("Average likes a post."),
  avgComments: z.number().min(0).optional().describe("Average comments a post."),
  engagementPosts: z
    .number()
    .int()
    .min(0)
    .max(1000)
    .optional()
    .describe("How many recent posts the averages and the rate are based on."),
  likesHidden: z.boolean().optional().describe("The account hides its like counts."),
  lastPostAt: when.optional().describe("When it last posted."),
  postsPerMonth: z.number().min(0).max(1000).optional().describe("How often it posts."),
  flags: z
    .array(z.enum(PROFILE_FLAGS))
    .optional()
    .describe(
      "What looks wrong: engagement_pod (the same few accounts comment on everything), bought_likes, brand_safety. Replaces the flags before; [] clears them.",
    ),
  notes: z
    .string()
    .trim()
    .max(1000)
    .optional()
    .describe("Brand-safety or other notes, in a sentence or two."),
  worksFor: z
    .array(
      z.object({
        brand: igHandle.describe("The brand's Instagram handle."),
        role: z.string().trim().max(80).optional().describe('e.g. "creative director".'),
      }),
    )
    .max(10)
    .optional()
    .describe("Brands the person works for or represents. Replaces the list before."),
  rejection: z
    .object({
      reason: z.enum(REJECT_REASONS),
      brand: igHandle
        .optional()
        .describe("Only turned down for this brand. Left out: for every brand."),
      note: z.string().trim().max(500).optional(),
    })
    .nullable()
    .optional()
    .describe(
      "Why you turned the account down, so it isn't checked again: a store, too small, a competitor's employee… null takes back every rejection.",
    ),
  samplePosts: z
    .array(instagramPost)
    .max(6)
    .default([])
    .describe("Up to 6 links to the account's posts that show what it posts."),
  mentions: z
    .array(
      z.object({
        brand: igHandle
          .optional()
          .describe(
            "The brand's Instagram handle. Left out: the demo's brand (or, for a competitor's poster, that competitor).",
          ),
        match: z
          .enum(MENTION_MATCHES)
          .optional()
          .describe(
            'How the brand shows: "caption" (named in the caption), "photo_tag" (tagged in the photo), "comment", or "competitor_post". Left out: caption (competitor_post for a competitor\'s poster).',
          ),
        post: instagramPost.describe("The post's Instagram link."),
        date: z.string().max(40).optional().describe("When it was posted, e.g. 2026-09-30."),
        likes: z.number().int().min(0).optional(),
        comments: z.number().int().min(0).optional(),
        plays: z.number().int().min(0).optional().describe("A reel's plays."),
        disclosure: z
          .array(z.enum(DISCLOSURES))
          .max(3)
          .optional()
          .describe(
            'How it\'s labelled as advertising: "ad" (#ad, #annonce, #reklame), "gifted", "paid_partnership" (Instagram\'s "Paid partnership" label).',
          ),
        collab: z.boolean().optional().describe("Posted as an Instagram collab with the brand."),
        imageUrl: httpsLink
          .optional()
          .describe("The post's picture. Brandwall keeps a small copy, since the link expires."),
      }),
    )
    .max(50)
    .default([])
    .describe("The posts where the account showed a brand."),
});
export type ResearchProfile = z.infer<typeof ResearchProfile>;

/** Accounts in the influencer database that fit (D-109). */
export const FindProfilesInput = z.object({
  niches: z
    .array(z.string().trim().min(1).max(40))
    .max(10)
    .optional()
    .describe('Any of these words in its niches, e.g. ["menswear", "basics"].'),
  location: z.string().trim().max(120).optional().describe('In its location, e.g. "Copenhagen".'),
  country: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/)
    .optional()
    .describe("Two-letter code, e.g. DK."),
  languages: z
    .array(
      z
        .string()
        .trim()
        .regex(/^[a-z]{2}$/),
    )
    .max(5)
    .optional()
    .describe('Writes in any of these, e.g. ["da"].'),
  accountTypes: z.array(z.enum(["personal", "creator", "business"])).optional(),
  minFollowers: z.number().int().min(0).optional(),
  maxFollowers: z.number().int().min(0).optional(),
  minEngagement: z.number().min(0).optional().describe("In percent."),
  maxEngagement: z.number().min(0).optional(),
  mentioned: z.array(igHandle).max(30).optional().describe("Showed any of these brands (handles)."),
  matches: z
    .array(z.enum(MENTION_MATCHES))
    .optional()
    .describe("Only count these kinds of mention for `mentioned`."),
  neverMentioned: z
    .array(igHandle)
    .max(30)
    .optional()
    .describe("Never showed any of these brands, e.g. the brand you pitch, or a competitor."),
  worksFor: igHandle.optional().describe("Works for this brand."),
  postedSince: when.optional().describe("Posted since this date: still active."),
  measuredSince: when
    .optional()
    .describe("Followers measured since this date. Older numbers may be stale."),
  seenSince: when.optional().describe("Researched (saved or refreshed) since this date."),
  forBrand: igHandle
    .optional()
    .describe("Who you look for: leaves out accounts turned down for this brand too."),
  includeRejected: z.boolean().default(false).describe("Also accounts that were turned down."),
  includeFlagged: z
    .boolean()
    .default(false)
    .describe("Also accounts flagged as an engagement pod, bought likes or brand safety."),
  sort: z
    .enum(["engagement", "followers", "followers_asc", "last_post", "last_seen"])
    .default("engagement"),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).max(100000).default(0),
});
export type FindProfilesInput = z.infer<typeof FindProfilesInput>;

/** Researched accounts, saved without a demo (D-109). */
export const SaveProfilesInput = z.object({
  profiles: z
    .array(ResearchProfile)
    .min(1)
    .max(100)
    .describe("Up to 100 accounts a call. Each mention needs its brand."),
});

/** Why a creator was chosen for a demo's report (D-104). */
export const ProspectChoice = z.object({
  score: z.number().int().min(0).max(100).describe("How good a fit, 0 to 100."),
  reason: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .describe("One or two sentences the brand reads: why this creator."),
  source: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .describe('How they were found, e.g. "#rains", "tagged @rains", "posts for @stutterheim".'),
});

export const ProspectsInput = z.object({
  profiles: z
    .array(
      ResearchProfile.extend({
        prospect: ProspectChoice.optional().describe(
          "Only for the 20 to 30 chosen for the demo's report. Leave it out for the rest.",
        ),
      }),
    )
    .min(1)
    .max(100)
    .describe("Every account you researched, up to 100 a call."),
  replace: z
    .boolean()
    .default(false)
    .describe("Forget the creators chosen for this demo before, and keep only those chosen here."),
});
export type ProspectsInput = z.infer<typeof ProspectsInput>;

/** "How you compare" (D-105): competitors' customer posts over a period. */
export const CompetitorsInput = z.object({
  competitors: z
    .array(
      z.object({
        handle: igHandle.describe("The competitor's Instagram handle."),
        name: z.string().trim().max(80).optional().describe("The competitor's name."),
        ugcCount: z
          .number()
          .int()
          .min(0)
          .describe("Customer posts for the competitor that you found in the period."),
        periodDays: z.number().int().min(1).max(3650).describe("The period, in days, e.g. 90."),
        topPost: z
          .object({
            link: instagramPost,
            imageUrl: httpsLink.optional(),
            username: igHandle.optional(),
            likes: z.number().int().min(0).optional(),
            comments: z.number().int().min(0).optional(),
          })
          .optional()
          .describe("The competitor's most liked customer post."),
        posters: z
          .array(ResearchProfile)
          .max(50)
          .default([])
          .describe(
            "The accounts that posted for the competitor. They go in the influencer database.",
          ),
      }),
    )
    .min(1)
    .max(10),
  yours: z
    .object({
      ugcCount: z.number().int().min(0),
      periodDays: z.number().int().min(1).max(3650),
    })
    .optional()
    .describe(
      "The brand's own customer posts, counted the same way and over the same period. Left out: the posts added to the demo.",
    ),
});
export type CompetitorsInput = z.infer<typeof CompetitorsInput>;

/** The outreach queue (D-107). */
export const OUTREACH_STATUSES = [
  "pending",
  "in-progress",
  "thin-skip",
  "ready-for-review",
  "approved",
  "sent",
  "failed",
] as const;
export type OutreachStatus = (typeof OUTREACH_STATUSES)[number];
export const OUTREACH_CHANNELS = ["email", "instagram", "linkedin", "phone", "other"] as const;

export const QueueInput = z.object({
  wave: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .describe('The wave these brands go out in, e.g. "1" or "2026-10 fashion".'),
  brands: z
    .array(
      z.object({
        brand: z.string().trim().min(1).max(80).describe("The brand's name."),
        website: z.string().trim().max(200).optional(),
        instagram: igHandle.optional(),
        contactName: z.string().trim().max(120).optional(),
        contactEmail: z.string().trim().email().max(254).optional(),
        channel: z.enum(OUTREACH_CHANNELS).default("email").describe("How to reach the contact."),
      }),
    )
    .min(1)
    .max(300),
});
export type QueueInput = z.infer<typeof QueueInput>;

/** One view (computer or phone) of a brand's homepage with room for the wall (D-070). */
export const DemoSiteView = z.object({
  /** The width the page was opened at, in CSS pixels. */
  width: z.number(),
  /** The page from the top down to the wall's heading. */
  top: z.object({ key: z.string(), height: z.number() }),
  /** The page's footer, under the wall. */
  bottom: z.object({ key: z.string(), height: z.number() }).nullable(),
  background: z.string(),
  text: z.string(),
  /** The width the site's content sits in. */
  wallWidth: z.number(),
  font: z.string().nullable(),
});
export type DemoSiteView = z.infer<typeof DemoSiteView>;

export const DemoSite = z.object({
  capturedAt: z.string(),
  url: z.string(),
  desktop: DemoSiteView.nullable(),
  phone: DemoSiteView.nullable(),
  /** Why the pictures couldn't be taken, for the admin. */
  error: z.string().nullable(),
});
export type DemoSite = z.infer<typeof DemoSite>;

export const BrandSuggestion = z.object({
  website: z.string(),
  name: z.string().nullable(),
  logoUrl: z.string().nullable(),
  colors: z.array(z.string()),
  fonts: z.array(z.string()),
});
export type BrandSuggestion = z.infer<typeof BrandSuggestion>;

export const ActivityEntry = z.object({
  id: z.string(),
  action: z.string(),
  summary: z.string(),
  actor: z.string(),
  /** Set when an AI app or API key did it. */
  app: z.string().nullable(),
  reason: z.string().nullable(),
  tool: z.string().nullable(),
  createdAt: z.string(),
  canUndo: z.boolean(),
  undone: z.boolean(),
  approval: z
    .object({
      id: z.string(),
      status: z.enum(["waiting", "approved", "rejected"]),
      decidedBy: z.string().nullable(),
    })
    .nullable(),
});
export type ActivityEntry = z.infer<typeof ActivityEntry>;

// The brand's own AI (screens 33, 40, Appendix A).
export const AiSettings = z.object({
  findPosts: z.boolean().default(true),
  approveWithinRules: z.boolean().default(true),
  tagProducts: z.boolean().default(true),
  createSchedule: z.boolean().default(true),
  draftMessages: z.boolean().default(true),
  acceptClearYes: z.boolean().default(false),
  publishWithoutApproval: z.boolean().default(false),
});
export type AiSettings = z.infer<typeof AiSettings>;

export const AI_SETTING_LABELS: Record<keyof AiSettings, string> = {
  findPosts: "Find and suggest posts",
  approveWithinRules: "Approve posts within your rules",
  tagProducts: "Tag products",
  createSchedule: "Create and schedule walls",
  draftMessages: "Draft permission messages",
  acceptClearYes: "Accept clear yes replies it finds in direct messages",
  publishWithoutApproval: "Publish without your approval",
};

export const Approval = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  summary: z.string(),
  reason: z.string().nullable(),
  requestedBy: z.string(),
  status: z.enum(["waiting", "approved", "rejected"]),
  decidedBy: z.string().nullable(),
  decidedVia: z.string().nullable(),
  result: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type Approval = z.infer<typeof Approval>;

export const ConnectedApp = z.object({
  id: z.string().uuid(),
  kind: z.enum(["mcp", "api_key"]),
  name: z.string(),
  lastActiveAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  /** API keys show their first characters only. */
  prefix: z.string().nullable(),
});
export type ConnectedApp = z.infer<typeof ConnectedApp>;

export const AiOverview = z.object({
  mcpUrl: z.string(),
  settings: AiSettings,
  apps: z.array(ConnectedApp),
  approvals: z.array(Approval),
  allowed: z.boolean(),
});
export type AiOverview = z.infer<typeof AiOverview>;

/** Everything Brandwall knows about one post (Appendix A 2, "Look at one post"). */
export const PostDetail = z.object({
  post: Post,
  requests: z.array(PermissionRequest),
  products: z.array(ProductTag),
  walls: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      live: z.boolean(),
      pinned: z.boolean(),
      hidden: z.boolean(),
    }),
  ),
});
export type PostDetail = z.infer<typeof PostDetail>;

// Seasons (screens 31, 32).
export const Schedule = z.object({
  id: z.string().uuid(),
  wallId: z.string().uuid(),
  wallName: z.string(),
  replacesWallId: z.string().uuid(),
  replacesWallName: z.string(),
  page: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  status: z.enum(["waiting", "scheduled", "cancelled"]),
  state: z.enum(["waiting", "upcoming", "live", "ended", "cancelled"]),
  postCount: z.number(),
  createdBy: z.string().nullable(),
  approvalId: z.string().uuid().nullable(),
});
export type Schedule = z.infer<typeof Schedule>;

export const Calendar = z.object({
  today: z.string(),
  schedules: z.array(Schedule),
  pages: z.array(z.object({ page: z.string(), wallId: z.string().uuid(), wallName: z.string() })),
  overlaps: z.array(z.object({ a: z.string().uuid(), b: z.string().uuid(), summary: z.string() })),
});
export type Calendar = z.infer<typeof Calendar>;

export const SEASONS = [
  "halloween",
  "black_friday",
  "christmas",
  "summer_sale",
  "easter",
  "valentines",
  "spring",
  "autumn",
  "winter",
  "summer",
] as const;

// Team (screen 38).
export const Member = z.object({
  userId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string(),
  role: Role,
  you: z.boolean(),
});
export const Invite = z.object({
  id: z.string().uuid(),
  email: z.string(),
  role: Role,
  createdAt: z.string(),
  expiresAt: z.string(),
  /** Only returned right after inviting, when email sending is off. */
  link: z.string().nullable(),
});
export const Team = z.object({
  members: z.array(Member),
  invites: z.array(Invite),
  seats: z.number(),
  seatsUsed: z.number(),
});
export type Team = z.infer<typeof Team>;

// Plans and billing (screen 39).
export const Billing = z.object({
  plan: Plan,
  paidPlan: Plan,
  trial: z.object({ plan: Plan, endsAt: z.string() }).nullable(),
  /** A plan Brandwall gave the brand by hand (D-079); until null means it doesn't end. */
  granted: z.object({ plan: Plan, until: z.string().nullable() }).nullable(),
  trialAvailable: z.boolean(),
  status: z.string().nullable(),
  interval: z.enum(["month", "year"]).nullable(),
  currentPeriodEnd: z.string().nullable(),
  cardPayments: z.boolean(),
  founding: z.object({ eligible: z.boolean(), left: z.number(), applied: z.boolean() }),
  wallsUsed: z.number(),
  wallNames: z.array(z.string()),
  agencyBrands: z.number(),
  /** Wall views this month, against the plan's monthly limit (null: none), and paused walls. */
  usage: z.object({
    viewsThisMonth: z.number(),
    viewLimit: z.number().nullable(),
    resetsOn: z.string(),
    pausedWalls: z.array(z.string()),
  }),
});
export type Billing = z.infer<typeof Billing>;

// Insights (screen 37) ---------------------------------------------------------------------

export const InsightDays = z.union([z.literal(7), z.literal(30), z.literal(90)]);

export const Metric = z.object({
  value: z.number(),
  previous: z.number(),
  /** Percent change against the period before, or null when there was nothing before. */
  change: z.number().nullable(),
});
export type Metric = z.infer<typeof Metric>;

export const InsightMetric = z.enum(["views", "opens", "clicks", "adds"]);
export type InsightMetric = z.infer<typeof InsightMetric>;

/** One line per number on how it is counted (PLAN.md: every number explains itself). */
export const INSIGHT_EXPLANATIONS: Record<InsightMetric | "freshness", string> = {
  views:
    "Each time a page with the wall loads and the wall shows posts. Bots and link previews are left out.",
  opens: "Taps on a post to see it larger. Counted once per post per page view.",
  clicks:
    "Taps on a product in a post: the View link, or Add to bag. Counted once per product per page view.",
  adds: "Taps on Add to bag that the shop confirmed. Only on Shopify, when the wall runs on the shop.",
  freshness:
    "100 when the newest live post is a day old or less, falling to 0 at 14 days. Stale after 7 days.",
};

export const Insights = z.object({
  wallId: z.string().nullable(),
  days: z.number(),
  from: z.string(),
  to: z.string(),
  monthlyEmail: z.boolean(),
  totals: z.object({
    views: Metric,
    opens: Metric,
    clicks: Metric,
    adds: Metric,
  }),
  series: z.array(
    z.object({
      day: z.string(),
      views: z.number(),
      opens: z.number(),
      clicks: z.number(),
      adds: z.number(),
    }),
  ),
  freshness: Freshness,
  topPosts: z.array(
    z.object({
      id: z.string(),
      creator: z.string(),
      previewUrl: z.string().nullable(),
      opens: z.number(),
      clicks: z.number(),
      adds: z.number(),
    }),
  ),
  topCreators: z.array(
    z.object({
      username: z.string(),
      posts: z.number(),
      opens: z.number(),
      clicks: z.number(),
    }),
  ),
  walls: z.array(z.object({ id: z.string(), name: z.string(), status: z.string() })),
  summary: z.string(),
});
export type Insights = z.infer<typeof Insights>;

export const InsightSettings = z.object({
  monthlyEmail: z.boolean().optional(),
});

// The blog on brandwall.app/blog (D-111), written in the admin pages or through the admin MCP.

/** "Why UGC sells — 2026!" → "why-ugc-sells-2026". Also takes a pasted address. */
export function slugify(text: string) {
  return text
    .trim()
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/?blog\//i, "")
    .replace(/[?#].*$/, "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa")
    .replace(/ß/g, "ss")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100)
    .replace(/-+$/, "");
}

export const BLOG_STATUSES = ["draft", "scheduled", "published"] as const;
export type BlogStatus = (typeof BLOG_STATUSES)[number];

/** A moment with its time zone, e.g. 2026-10-12T09:00:00+02:00. */
export const BlogTime = z
  .string()
  .max(40)
  .refine(
    (v) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(v) && !Number.isNaN(Date.parse(v)),
    "Use a time with its time zone, like 2026-10-12T09:00:00+02:00.",
  );

const blogFields = {
  title: z.string().trim().min(1).max(200).describe("The headline."),
  slug: z
    .string()
    .trim()
    .max(200)
    .describe(
      "The post's address: brandwall.app/blog/<slug>. Lowercase words with dashes, e.g. ugc-for-shopify. Made from the title when left out.",
    ),
  body: z
    .string()
    .max(200_000)
    .describe(
      "The post in Markdown: ## headings, lists, links, > quotes, tables, and pictures as ![alt](url) with a url from upload_blog_image. Don't repeat the title as a heading.",
    ),
  excerpt: z
    .string()
    .trim()
    .max(500)
    .describe("One or two sentences for the blog's front page and the feed."),
  seoTitle: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .describe(
      "Title for search results, when it should differ from the headline. Up to 60 characters.",
    ),
  seoDescription: z
    .string()
    .trim()
    .max(300)
    .nullable()
    .describe("Description for search results and link previews. 120 to 160 characters."),
  coverImageId: z
    .string()
    .uuid()
    .nullable()
    .describe("The picture at the top and in link previews: an id from upload_blog_image."),
  coverAlt: z
    .string()
    .trim()
    .max(300)
    .describe("What the cover picture shows, for screen readers."),
  author: z.string().trim().min(1).max(100).describe('Who wrote it, e.g. "Janus".'),
  tags: z.array(z.string().trim().min(1).max(40)).max(10).describe("Up to 10 topics."),
};

export const BlogPostInput = z.object({
  ...blogFields,
  slug: blogFields.slug.optional(),
  body: blogFields.body.default(""),
  excerpt: blogFields.excerpt.default(""),
  seoTitle: blogFields.seoTitle.optional(),
  seoDescription: blogFields.seoDescription.optional(),
  coverImageId: blogFields.coverImageId.optional(),
  coverAlt: blogFields.coverAlt.default(""),
  author: blogFields.author.optional(),
  tags: blogFields.tags.default([]),
  publishAt: BlogTime.nullable()
    .optional()
    .describe(
      "Leave out to keep it a draft. A time in the future schedules it; now or earlier publishes it.",
    ),
});
export type BlogPostInput = z.infer<typeof BlogPostInput>;

export const BlogPostPatch = z.object({
  title: blogFields.title.optional(),
  slug: blogFields.slug.optional(),
  body: blogFields.body.optional(),
  excerpt: blogFields.excerpt.optional(),
  seoTitle: blogFields.seoTitle.optional(),
  seoDescription: blogFields.seoDescription.optional(),
  coverImageId: blogFields.coverImageId.optional(),
  coverAlt: blogFields.coverAlt.optional(),
  author: blogFields.author.optional(),
  tags: blogFields.tags.optional(),
});
export type BlogPostPatch = z.infer<typeof BlogPostPatch>;

export const BlogImageInput = z
  .object({
    url: z.string().url().max(2000).optional().describe("A public https link to the picture."),
    data: z
      .string()
      .max(20_000_000)
      .optional()
      .describe("Or the picture itself: base64, or a data: URL. PNG, JPEG, WebP, GIF or AVIF."),
    alt: z.string().trim().max(300).default("").describe("What the picture shows."),
  })
  .refine((v) => Boolean(v.url) !== Boolean(v.data), "Send either url or data.");
export type BlogImageInput = z.infer<typeof BlogImageInput>;

export interface BlogImage {
  id: string;
  /** Its address on brandwall.app. */
  url: string;
  width: number;
  height: number;
  alt: string;
  createdAt: string;
}

export interface BlogPostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  status: BlogStatus;
  publishedAt: string | null;
  updatedAt: string;
  url: string;
  tags: string[];
  author: string;
}

export interface BlogPost extends BlogPostSummary {
  body: string;
  seoTitle: string | null;
  seoDescription: string | null;
  coverImageId: string | null;
  coverUrl: string | null;
  coverAlt: string;
  previewUrl: string;
  /** Old addresses that forward here. */
  oldSlugs: string[];
  createdAt: string;
  /** Things to fix before it goes live: missing descriptions, links to posts that aren't there… */
  warnings: string[];
}
